import { useMemo, useState } from "react";
import { Dismiss20Regular } from "@fluentui/react-icons";
import { APP_LOCALE } from "../../../config/locale";
import type { Client } from "../../../clients/clientsApi";
import type { WorkStatus } from "../../../obras/obrasApi";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../../components/SearchableSelect";

export interface WorkEditValues {
  clienteId: number | "";
  nomeObra: string;
  localObra: string;
  workStatusId: number | null;
  dataInicio: string | null;
  dataFim: string | null;
  valorTotal: number | null;
  valorGasto: number | null;
  progresso: number | null;
}

interface WorkEditDrawerProps {
  initial: WorkEditValues;
  clients: Client[];
  statuses: WorkStatus[];
  isSaving: boolean;
  error: string | null;
  onSave: (values: WorkEditValues) => void;
  onClose: () => void;
}

const normalizeNumberInput = (value: string): string => {
  const cleaned = value.trim().replace(/\s/g, "");
  if (!cleaned) return "";
  const hasComma = cleaned.includes(",");
  const hasDot = cleaned.includes(".");
  if (hasComma && hasDot) return cleaned.replace(/\./g, "").replace(",", ".");
  if (hasComma) return cleaned.replace(",", ".");
  return cleaned;
};

const parseNumberInput = (value: string): number => {
  const normalized = normalizeNumberInput(value);
  if (!normalized) return 0;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumberInput = (value: number | null, decimals: number): string => {
  if (value === null || value === undefined) return "";
  return value.toLocaleString(APP_LOCALE, {
    useGrouping: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

const formatDateInput = (value?: string | null): string => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const normalizeDateValue = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const inputClass =
  "w-full px-3 py-2 border border-border rounded bg-background text-foreground text-sm";
const labelClass = "flex flex-col gap-1 text-sm text-secondary-text";

export function WorkEditDrawer({
  initial,
  clients,
  statuses,
  isSaving,
  error,
  onSave,
  onClose,
}: WorkEditDrawerProps) {
  const [draft, setDraft] = useState<WorkEditValues>(initial);
  const [numDrafts, setNumDrafts] = useState<Record<string, string>>({});
  const numValue = (key: string, value: number | null) =>
    numDrafts[key] ?? formatNumberInput(value, 2);
  const clearNumDraft = (key: string) =>
    setNumDrafts((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  const onNumChange = (
    key: string,
    raw: string,
    apply: (n: number | null) => void,
    clamp?: (n: number) => number,
  ) => {
    setNumDrafts((prev) => ({ ...prev, [key]: raw }));
    const normalized = normalizeNumberInput(raw);
    if (!normalized) {
      apply(null);
      return;
    }
    const parsed = parseNumberInput(raw);
    apply(clamp ? clamp(parsed) : parsed);
  };
  const update = (patch: Partial<WorkEditValues>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const clientOptions = useMemo<SearchableSelectOption<number>[]>(
    () => clients.map((client) => ({ value: client.id, label: client.name })),
    [clients],
  );

  const canSave = !!draft.clienteId && draft.nomeObra.trim() !== "";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex justify-end"
      onClick={onClose}
    >
      <div
        className="bg-card border-l border-border h-full w-full max-w-md overflow-y-auto p-6 space-y-4"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3
            className="text-foreground"
            style={{
              fontSize: "var(--text-section-title)",
              fontWeight: "var(--font-weight-semi-bold)",
            }}
          >
            Editar obra
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="p-2 hover:bg-accent transition-colors rounded"
            style={{ borderRadius: "var(--radius)" }}
          >
            <Dismiss20Regular className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div
            className="border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {error}
          </div>
        )}

        <SearchableSelect<number>
          label="Cliente"
          required
          labelClassName="text-sm text-secondary-text"
          labelStyle={{}}
          value={draft.clienteId === "" ? null : draft.clienteId}
          onChange={(next) => update({ clienteId: next ?? "" })}
          options={clientOptions}
          placeholder="Selecione o cliente"
          searchPlaceholder="Procurar cliente…"
        />

        <label className={labelClass}>
          Nome da Obra *
          <input
            type="text"
            value={draft.nomeObra}
            onChange={(event) => update({ nomeObra: event.target.value })}
            className={inputClass}
          />
        </label>

        <label className={labelClass}>
          Local
          <input
            type="text"
            value={draft.localObra}
            onChange={(event) => update({ localObra: event.target.value })}
            className={inputClass}
          />
        </label>

        <label className={labelClass}>
          Estado
          <select
            value={draft.workStatusId ?? ""}
            onChange={(event) =>
              update({ workStatusId: event.target.value ? Number(event.target.value) : null })
            }
            className={inputClass}
          >
            {statuses.map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className={labelClass}>
            Data de Início
            <input
              type="date"
              value={formatDateInput(draft.dataInicio)}
              onChange={(event) =>
                update({ dataInicio: normalizeDateValue(event.target.value) })
              }
              className={inputClass}
            />
          </label>
          <label className={labelClass}>
            Conclusão Prevista
            <input
              type="date"
              value={formatDateInput(draft.dataFim)}
              onChange={(event) =>
                update({ dataFim: normalizeDateValue(event.target.value) })
              }
              className={inputClass}
            />
          </label>
        </div>

        <label className={labelClass}>
          Valor Adjudicado (€)
          <input
            type="text"
            inputMode="decimal"
            value={numValue("valorTotal", draft.valorTotal)}
            onChange={(event) =>
              onNumChange("valorTotal", event.target.value, (n) => update({ valorTotal: n }))
            }
            onBlur={() => clearNumDraft("valorTotal")}
            className={inputClass}
            placeholder="—"
          />
        </label>

        <label className={labelClass}>
          Custo Total Atual (€)
          <input
            type="text"
            inputMode="decimal"
            value={numValue("valorGasto", draft.valorGasto)}
            onChange={(event) =>
              onNumChange("valorGasto", event.target.value, (n) => update({ valorGasto: n }))
            }
            onBlur={() => clearNumDraft("valorGasto")}
            className={inputClass}
            placeholder="—"
          />
        </label>

        <label className={labelClass}>
          Progresso (%)
          <input
            type="text"
            inputMode="decimal"
            value={numValue("progresso", draft.progresso)}
            onChange={(event) =>
              onNumChange(
                "progresso",
                event.target.value,
                (n) => update({ progresso: n }),
                (n) => Math.max(0, Math.min(100, n)),
              )
            }
            onBlur={() => clearNumDraft("progresso")}
            className={inputClass}
            placeholder="0"
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors min-h-[44px]"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onSave(draft)}
            disabled={isSaving || !canSave}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            {isSaving ? "A guardar..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
